<template>
	<div class="leaderboard" :class="{ 'failed-loading': !usersLoaded }">
		<div class="leaderboard__title">{{ translate("ui.ranking") }}</div>
		<HorizontalDecorDivider />
		<template v-if="usersLoaded === true">
			<div class="leaderboard__buttons">
				<AppTextButton
					class="leaderboard__btn"
					:type="
						isGlobal
							? StatusTypes.DEFAULT_ACTIVE
							: StatusTypes.DEFAULT
					"
					:text="translate('ui.world')"
					@click="isGlobal = true"
				/>
				<AppTextButton
					class="leaderboard__btn"
					:type="
						!isGlobal
							? StatusTypes.DEFAULT_ACTIVE
							: StatusTypes.DEFAULT
					"
					:text="translate('ui.country')"
					@click="isGlobal = false"
				/>
			</div>
			<div class="leaderboard__item-header">
				{{ translate("ui.top_players") }}
			</div>
			<div class="leaderboard__container">
				<LeaderboardRow
					class="leaderboard__item-subheader"
					:rank="'#'"
					:country="''"
					:name="translate('ui.player')"
					:score="translate('ui.income')"
					bold
				/>
				<HorizontalDecorDivider :with-sides="false" />
				<div
					class="leaderboard__item"
					v-for="(item, i) in users1"
					:key="i"
				>
					<LeaderboardRow
						:skin="getSkin(item, i)"
						:rank="item.placement > 0 ? item.placement : ''"
						:country="item.country"
						:name="resolveName(item)"
						:score="item.score"
					/>
					<HorizontalDecorDivider :with-sides="false" />
				</div>
				<HorizontalDecorDivider
					v-if="users2.length > 0"
					class="leaderboard__divider-mid"
				/>
				<div
					class="leaderboard__item"
					v-for="(item, i) in users2"
					:key="i"
				>
					<LeaderboardRow
						:skin="getSkin(item, i)"
						:rank="item.placement > 0 ? item.placement : ''"
						:country="item.country"
						:name="resolveName(item)"
						:score="item.score"
					/>
					<HorizontalDecorDivider :with-sides="false" />
				</div>
			</div>
			<!-- TODO: Sticky footer bez fade -->
			<div class="leaderboard__footer">
				<div class="icon-info-circle" />
				<span class="leaderboard__info">
					{{
						translate("ui.ranking_update_minutes").replace(
							"{number}",
							"30",
						)
					}}
				</span>
			</div>
		</template>
		<template v-else-if="usersLoaded === false">
			<div class="failed-loading__container">
				<div class="failed-loading__header">
					{{ translate("ui.username") }}
				</div>
				<div class="failed-loading__perex">
					{{ translate("ui.ranking_username_required") }}
				</div>
				<AppTextButton
					class="failed-loading__button"
					:type="StatusTypes.PRIMARY"
					:text="translate('ui.enter_name')"
					@click="showChangeNamePopup"
				/>
			</div>
		</template>
		<template v-else>
			<div class="failed-loading__container">
				<div class="failed-loading__header">
					{{ translate("ui.loading.non_translatable") }}
				</div>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import LeaderboardRow from "@/components/Leaderboard/LeaderboardRow.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import useSocketService from "@/hooks/useSocketService";
import { PopupType, usePopupStore } from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, ref } from "vue";
import type { Leaderboard, LeaderboardResult } from "../../../types";

const socketService = markRaw(useSocketService());
const userStore = useUserStore();
const runtimeStore = useRuntimeStore();
const { nameChanged, userName, userId } = storeToRefs(userStore);

const isGlobal = ref<boolean>(true);

const usersLoaded = ref<boolean | null>(null);

const users1 = computed((): Leaderboard[] => {
	return isGlobal.value ? users1Global.value : users1Country.value;
});
const users2 = computed((): Leaderboard[] => {
	return isGlobal.value ? users2Global.value : users2Country.value;
});

const users1Country = ref<Leaderboard[]>([]);
const users2Country = ref<Leaderboard[]>([]);

const users1Global = ref<Leaderboard[]>([]);
const users2Global = ref<Leaderboard[]>([]);

const loadUsers = (): void => {
	const cachedData = runtimeStore.leaderboardData;
	if (
		cachedData !== null &&
		runtimeStore.leaderboardLoadTimestamp &&
		runtimeStore.leaderboardLoadTimestamp > Date.now() - 1000 * 60 * 5
	) {
		setUsersData(cachedData);
		return;
	}
	socketService.getLeaderboard().then((data: LeaderboardResult): void => {
		if (data) {
			runtimeStore.setLeaderboardData(data);
			setUsersData(data);
		}
	});
};

const setUsersData = (data: LeaderboardResult): void => {
	const users2ServedGlobal = data.globalAroundPlayer.length > 0;
	const users2ServedCountry = data.globalAroundPlayer.length > 0;
	users1Global.value = sortUsers(data.globalTop20, users2ServedGlobal);
	users1Country.value = sortUsers(data.countryTop20, users2ServedCountry);
	const playerPlacementGlobal = data.globalAroundPlayer.find(
		(user: Leaderboard): boolean => user.user_id === userId.value,
	)?.placement;
	const playerPlacementCountry = data.countryAroundPlayer.find(
		(user: Leaderboard): boolean => user.user_id === userId.value,
	)?.placement;
	if (playerPlacementGlobal && playerPlacementGlobal > 30) {
		users2Global.value = sortUsers(
			data.globalAroundPlayer,
			users2ServedGlobal,
		);
	}
	if (playerPlacementCountry && playerPlacementCountry > 30) {
		users2Country.value = sortUsers(
			data.countryAroundPlayer,
			users2ServedCountry,
		);
	}

	usersLoaded.value = true;
};

const resolveName = (user: Leaderboard): string => {
	return user.user_id === userId.value ? userName.value : user.user_name;
};

const sortUsers = (
	users: Leaderboard[],
	users2Served: boolean,
): Leaderboard[] => {
	const bestPlacement = users[0]?.placement || 1;
	// Make a shallow copy of the array and its objects
	let usersCopy = users.map(
		(user: Leaderboard): Leaderboard => ({
			...user,
		}),
	);

	const player = usersCopy.find((user: Leaderboard): boolean => {
		return user.user_id === userId.value;
	});
	if (player) {
		player.user_name = userName.value;
		player.score = useResourcesStore().coinsIncome ?? 0;
		player.country = useUserStore().country;
	} else if (!users2Served) {
		usersCopy.push({
			placement: -1,
			user_name: userName.value,
			user_id: userId.value,
			country: userStore.country,
			score: useResourcesStore().coinsIncome ?? 0,
			country_placement: 1,
		});
	}

	usersCopy.sort((a: Leaderboard, b: Leaderboard): number => {
		return b.score - a.score;
	});

	let rankCorrection = 0;
	usersCopy = usersCopy.map((user: Leaderboard, i: number): Leaderboard => {
		if (!users2Served && user.user_id === userId.value) {
			rankCorrection = 1;
			return user;
		}
		user.placement = bestPlacement + i - rankCorrection;
		return user;
	});

	return usersCopy;
};

const getSkin = (user: Leaderboard, i: number): SkinTypes | null => {
	if (user.user_id === userId.value) return SkinTypes.LIGHT;
	if (i % 2 === 0) return SkinTypes.DARK;
	return null;
};

const showChangeNamePopup = (): void => {
	usePopupStore().showPopup({
		type: PopupType.CHANGE_NAME,
		dropOnInterrupt: true,
		heading: translate("ui.username"),
		height: 13.75,
		isPaddingless: true,
		closeCallback: async (): Promise<void> => {
			if (!nameChanged.value) return;
			loadUsers();
		},
	});
};

onMounted((): void => {
	if (nameChanged.value) {
		loadUsers();
		return;
	} else {
		usersLoaded.value = false;
	}
});
</script>

<style lang="scss" scoped>
$baseTexture: url($path-images + "backgrounds/textures/window-texture-bg.avif")
	repeat center;

.leaderboard {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.5rem;
	padding: 1.25rem;
	padding-top: 0;

	&__buttons {
		@include flex-row-center;
		gap: 1.25rem;

		.leaderboard__btn {
			width: 7.875rem;
		}
	}

	&__container {
		width: 100%;
	}
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
	}
	&__item {
		width: 100%;
		&-subheader {
			width: 100%;
			@include flex-row-between;
			* {
				font-weight: 800;
			}
			.leaderboard-row__name {
				flex: 1;
			}
		}
		&-header {
			color: #f4d278;
			font-weight: bold;
			font-size: 1rem;
			text-transform: uppercase;
		}
		&-title {
			width: 80%;
		}
		&-text {
			color: #fff3dd;
		}
	}
	&__footer {
		@include flex-row-center;
		gap: 0.25rem;
		width: 100%;
		height: 2rem;
	}

	:deep(.horizontal-decor-divider) {
		width: 100%;
	}

	&__divider-mid {
		margin: 0.5rem 0;

		:deep(.cls-1) {
			fill: #f4d278;
		}
	}

	&.failed-loading {
		justify-content: center;
		height: 45vh;
	}
	.failed-loading {
		&__container {
			flex: 1;
			@include flex-col-center;
			gap: 0.5rem;
		}
		&__header {
			font-weight: 800;
			font-size: 1.125rem;
		}
		&__perex {
			margin-bottom: 1rem;
			font-size: 1rem;
			text-align: center;
		}
	}
}
</style>
